import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { firestore } from '../firebase';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  TrendingUpOutlined,
  FeedbackOutlined,
  StarOutlined,
  DownloadOutlined,
  CalendarTodayOutlined,
  AssessmentOutlined
} from '@mui/icons-material';

const Dashboard = () => {
  const { company } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)); // 7 days ago
  const [endDate, setEndDate] = useState(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [recentFeedbacks, setRecentFeedbacks] = useState([]);

  useEffect(() => {
    if (company?.id) {
      fetchFeedbacks();
      fetchRecentFeedbacks();
    }
  }, [company?.id, startDate, endDate]);

  const fetchFeedbacks = async () => {
    if (!company?.id) return;
    
    setLoading(true);
    try {
      const feedbackRef = collection(firestore, 'feedback');
      const q = query(
        feedbackRef,
        where('companyId', '==', company.id),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
      console.log('Fetching feedbacks with query:', company.id);
      const querySnapshot = await getDocs(q);
      const feedbackList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFeedbacks(feedbackList);
      calculateAnalytics(feedbackList);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentFeedbacks = async () => {
    if (!company?.id) return;
    
    try {
      const feedbackRef = collection(firestore, 'feedback');
      const q = query(
        feedbackRef,
        where('companyId', '==', company.id),
        orderBy('date', 'desc'),
        limit(5)
      );
      
      const querySnapshot = await getDocs(q);
      const recentList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setRecentFeedbacks(recentList);
    } catch (error) {
      console.error('Error fetching recent feedbacks:', error);
    }
  };

  const calculateAnalytics = (feedbackList) => {
    const total = feedbackList.length;
    const countByType = { 
      excellent: 0, 
      veryGood: 0, 
      good: 0, 
      average: 0, 
      poor: 0 
    };

    feedbackList.forEach(feedback => {
      if (feedback.excellent) countByType.excellent++;
      else if (feedback.veryGood) countByType.veryGood++;
      else if (feedback.good) countByType.good++;
      else if (feedback.average) countByType.average++;
      else if (feedback.poor) countByType.poor++;
    });

    const satisfactionRate = total > 0 ? 
      ((countByType.excellent + countByType.veryGood + countByType.good) / total * 100).toFixed(1) : 0;

    setAnalytics({
      total,
      countByType,
      satisfactionRate
    });
  };

  const getFeedbackType = (feedback) => {
    if (feedback.excellent) return { type: "EXCELLENT", color: "success" };
    if (feedback.veryGood) return { type: "VERY GOOD", color: "primary" };
    if (feedback.good) return { type: "GOOD", color: "info" };
    if (feedback.average) return { type: "AVERAGE", color: "warning" };
    if (feedback.poor) return { type: "POOR", color: "error" };
    return { type: "N/A", color: "default" };
  };

  const exportToExcel = () => {
    const transformedData = feedbacks.map(feedback => {
      let datePart = '';
      let timePart = '';
      if (feedback.date && feedback.date.toDate) {
        const dt = feedback.date.toDate();
        datePart = dt.toLocaleDateString();
        timePart = dt.toLocaleTimeString();
      }
      
      const feedbackType = getFeedbackType(feedback).type;
      
      return {
        DOCUMENT_ID: feedback.id,
        DATE: datePart,
        TIME: timePart,
        FEEDBACK: feedbackType,
        DESCRIPTION: feedback.description || '',
      };
    });

    const summaryData = Object.entries(analytics.countByType || {}).map(([type, count]) => {
      const percentage = analytics.total ? ((count / analytics.total) * 100).toFixed(2) + '%' : '0%';
      return {
        Feedback: type.toUpperCase(),
        Count: count,
        Percentage: percentage,
      };
    });

    const wsFeedback = XLSX.utils.json_to_sheet(transformedData);
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    
    const workbook = { 
      Sheets: { 
        'Feedback': wsFeedback, 
        'Summary': wsSummary 
      },
      SheetNames: ['Feedback', 'Summary']
    };

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `feedback_${company.name}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Dashboard - {company?.name}
      </Typography>

      {/* Analytics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FeedbackOutlined sx={{ mr: 1 }} />
                <Typography variant="h6">Total Feedback</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {analytics.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpOutlined sx={{ mr: 1 }} />
                <Typography variant="h6">Satisfaction Rate</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {analytics.satisfactionRate || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StarOutlined sx={{ mr: 1 }} />
                <Typography variant="h6">Excellent Reviews</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {analytics.countByType?.excellent || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentOutlined sx={{ mr: 1 }} />
                <Typography variant="h6">Poor Reviews</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {analytics.countByType?.poor || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Export */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <CalendarTodayOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filter & Export
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Start Date</Typography>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="yyyy/MM/dd"
                placeholderText="Select start date"
                customInput={
                  <Button variant="outlined" sx={{ minWidth: 150 }}>
                    {startDate ? startDate.toLocaleDateString() : 'Select start date'}
                  </Button>
                }
              />
            </Box>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>End Date</Typography>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="yyyy/MM/dd"
                placeholderText="Select end date"
                customInput={
                  <Button variant="outlined" sx={{ minWidth: 150 }}>
                    {endDate ? endDate.toLocaleDateString() : 'Select end date'}
                  </Button>
                }
              />
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<DownloadOutlined />}
                onClick={exportToExcel}
                sx={{ 
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                  }
                }}
              >
                Export to Excel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Recent Feedbacks */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Feedback ({feedbacks.length} total)
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Feedback Type</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedbacks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((feedback) => {
                    const feedbackInfo = getFeedbackType(feedback);
                    return (
                      <TableRow key={feedback.id}>
                        <TableCell>
                          {feedback.date && feedback.date.toDate ? (
                            <Box>
                              <Typography variant="body2">
                                {feedback.date.toDate().toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {feedback.date.toDate().toLocaleTimeString()}
                              </Typography>
                            </Box>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={feedbackInfo.type} 
                            color={feedbackInfo.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {feedback.description || 'No description'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={feedbacks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
